import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { http, CURRENCIES, currencySymbol, fmtMoney, openPdf } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card } from "../components/ui/card";
import { Plus, Trash2, Plane, Hotel, ArrowLeft, Save, Download, Loader2, DollarSign, Users } from "lucide-react";
import { toast } from "sonner";
import ImagesInput from "../components/quote/ImagesInput";

const emptyFlight = {
  date: "", origin: "", destination: "", departure_time: "", arrival_time: "",
  baggage: "", airline: "", notes: "",
  price_per_person: 0, price_total: 0, show_price: false,
};
const emptyHotel = {
  name: "", nights: 1, images: [], description: "",
  room_type: "", meal_plan: "", price_per_person: 0, price_total: 0,
  option_label: "", group: "",
};

const BAGGAGE_OPTIONS = ["Mochila (personal)", "Cabina 10kg", "Bodega 23kg", "Bodega 15kg", "Cabina + Bodega"];
const MEAL_PLANS = ["Solo Alojamiento", "Desayuno", "Media Pensión", "Pensión Completa", "All Inclusive"];
const ROOM_TYPES = ["Individual", "Doble Estándar", "Doble Superior", "Triple", "Familiar", "Suite"];

const Section = ({ n, icon: Icon, title, subtitle, children, actions }) => (
  <section className="fade-up bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
    <header className="flex items-start justify-between gap-4 mb-6 flex-wrap">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center shrink-0">
          <Icon className="text-sky-600" size={18} />
        </div>
        <div>
          <p className="text-[11px] font-bold uppercase tracking-widest text-sky-600">Paso {n}</p>
          <h2 className="font-serif-display text-2xl font-bold text-slate-900">{title}</h2>
          {subtitle && <p className="text-sm text-slate-600 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {actions}
    </header>
    {children}
  </section>
);

const Field = ({ label, children, className = "" }) => (
  <div className={`flex flex-col gap-1.5 ${className}`}>
    <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">{label}</Label>
    {children}
  </div>
);

const QuoteFormPage = () => {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);

  const [form, setForm] = useState({
    date: today,
    passenger_name: "",
    destination: "",
    passengers: 1,
    currency: "USD",
    flights: [],
    hotels: [],
    notes: "",
    show_total: true,
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  useEffect(() => {
    if (!isEdit) return;
    http.get(`/quotes/${id}`).then((r) => setForm({
      passengers: 1, show_total: true, ...r.data,
    })).catch(() => toast.error("No se pudo cargar")).finally(() => setLoading(false));
  }, [id, isEdit]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // Auto-recompute hotel totals when passengers or price_per_person changes
  const updateFlight = (i, k, v) => setForm((p) => {
    const flights = p.flights.map((f, idx) => {
      if (idx !== i) return f;
      const next = { ...f, [k]: v };
      // Auto total = ppp * passengers, unless user just edited price_total manually
      if (k === "price_per_person") {
        next.price_total = (Number(v) || 0) * (Number(p.passengers) || 1);
      }
      return next;
    });
    return { ...p, flights };
  });

  const updateHotel = (i, k, v) => setForm((p) => {
    const hotels = p.hotels.map((h, idx) => {
      if (idx !== i) return h;
      const next = { ...h, [k]: v };
      if (k === "price_per_person") {
        next.price_total = (Number(v) || 0) * (Number(p.passengers) || 1);
      }
      return next;
    });
    return { ...p, hotels };
  });

  // When passengers changes, recompute all price_totals from price_per_person
  const setPassengers = (v) => {
    const passengers = Math.max(1, parseInt(v || "1", 10));
    setForm((p) => ({
      ...p,
      passengers,
      flights: p.flights.map((f) => ({ ...f, price_total: (Number(f.price_per_person) || 0) * passengers })),
      hotels: p.hotels.map((h) => ({ ...h, price_total: (Number(h.price_per_person) || 0) * passengers })),
    }));
  };

  const submit = async (thenDownload = false) => {
    if (!form.passenger_name.trim() || !form.destination.trim()) {
      toast.error("Nombre del pasajero y destino son obligatorios");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        passengers: Math.max(1, Number(form.passengers) || 1),
        flights: form.flights.map((f) => ({
          ...f,
          price_per_person: Number(f.price_per_person) || 0,
          price_total: Number(f.price_total) || 0,
          show_price: !!f.show_price,
        })),
        hotels: form.hotels.map((h) => ({
          ...h,
          nights: Number(h.nights) || 0,
          price_per_person: Number(h.price_per_person) || 0,
          price_total: Number(h.price_total) || 0,
          images: (h.images || []).map((img) => typeof img === "string" ? img : img.value),
        })),
      };
      const res = isEdit
        ? await http.put(`/quotes/${id}`, payload)
        : await http.post(`/quotes`, payload);
      toast.success(isEdit ? "Cotización actualizada" : "Cotización creada");
      if (thenDownload) {
        openPdf(`/quotes/${res.data.id}/pdf`, `cotizacion_${res.data.passenger_name || res.data.id}.pdf`)
          .catch(() => toast.error("No se pudo descargar el PDF"));
      }
      navigate("/");
    } catch (e) {
      toast.error("Error al guardar");
    } finally { setSaving(false); }
  };

  const hotelsForRender = useMemo(() => form.hotels.map((h) => ({
    ...h,
    images: (h.images || []).map((img) => typeof img === "string"
      ? { type: img.startsWith("http") ? "url" : "upload", value: img }
      : img),
  })), [form.hotels]);

  if (loading) return <p className="text-slate-500">Cargando…</p>;

  const totalHotels = form.hotels.reduce((s, h) => s + Number(h.price_total || 0), 0);
  const totalFlights = form.flights.filter((f) => f.show_price).reduce((s, f) => s + Number(f.price_total || 0), 0);
  const grandTotal = totalHotels + totalFlights;

  return (
    <div className="max-w-5xl mx-auto pb-24">
      <div className="fade-up flex items-center gap-3 mb-6">
        <Button variant="ghost" onClick={() => navigate("/")} className="rounded-xl" data-testid="btn-back">
          <ArrowLeft size={18} className="mr-1" /> Volver
        </Button>
      </div>

      <header className="fade-up mb-8">
        <p className="text-[11px] font-bold uppercase tracking-widest text-sky-600 mb-2">
          {isEdit ? "Editar cotización" : "Nueva cotización"}
        </p>
        <h1 className="font-serif-display text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">
          Confecciona tu cotización
        </h1>
        <p className="mt-2 text-slate-600 max-w-2xl">Completa los datos del pasajero, vuelos y hoteles. Descarga el PDF profesional con un click.</p>
      </header>

      <div className="space-y-6">
        <Section n={1} icon={Users} title="Datos generales" subtitle="Información del pasajero y de la cotización">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
            <Field label="Fecha" className="lg:col-span-2">
              <Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className="rounded-xl" data-testid="input-date" />
            </Field>
            <Field label="Nombre del pasajero" className="sm:col-span-2 lg:col-span-3">
              <Input placeholder="Ej. Juan Pérez" value={form.passenger_name} onChange={(e) => set("passenger_name", e.target.value)} className="rounded-xl" data-testid="input-passenger" />
            </Field>
            <Field label="Cant. pasajeros">
              <Input type="number" min="1" step="1" value={form.passengers} onChange={(e) => setPassengers(e.target.value)} className="rounded-xl" data-testid="input-passengers" />
            </Field>
            <Field label="Destino" className="sm:col-span-2 lg:col-span-4">
              <Input placeholder="Ej. Cancún, México" value={form.destination} onChange={(e) => set("destination", e.target.value)} className="rounded-xl" data-testid="input-destination" />
            </Field>
            <Field label="Moneda" className="lg:col-span-2">
              <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
                <SelectTrigger data-testid="select-currency-type" className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
        </Section>

        <Section
          n={2}
          icon={Plane}
          title="Vuelos"
          subtitle="Múltiples tramos con precio opcional"
          actions={
            <Button onClick={() => set("flights", [...form.flights, { ...emptyFlight }])} className="rounded-xl bg-sky-600 hover:bg-sky-700" data-testid="button-add-flight">
              <Plus size={16} className="mr-1" /> Añadir vuelo
            </Button>
          }
        >
          {form.flights.length === 0 && (
            <p className="text-sm text-slate-500 italic">Aún no hay vuelos. Añade el primer tramo.</p>
          )}
          <div className="space-y-4">
            {form.flights.map((f, i) => (
              <Card key={i} className="p-5 border border-slate-200 rounded-2xl bg-slate-50/50" data-testid={`flight-card-${i}`}>
                <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
                  <span className="text-xs font-bold uppercase tracking-widest text-sky-600">Vuelo #{i + 1}</span>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 rounded-full bg-white border border-slate-200 px-3 py-1">
                      <DollarSign size={13} className={f.show_price ? "text-amber-600" : "text-slate-400"} />
                      <span className="text-xs font-semibold text-slate-700">Mostrar precio</span>
                      <Switch checked={!!f.show_price} onCheckedChange={(v) => updateFlight(i, "show_price", v)} data-testid={`flight-show-price-${i}`} />
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => set("flights", form.flights.filter((_, idx) => idx !== i))} className="text-rose-600" data-testid={`flight-remove-${i}`}>
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  <Field label="Fecha"><Input type="date" value={f.date} onChange={(e) => updateFlight(i, "date", e.target.value)} className="rounded-xl bg-white" /></Field>
                  <Field label="Origen"><Input placeholder="EZE" value={f.origin} onChange={(e) => updateFlight(i, "origin", e.target.value)} className="rounded-xl bg-white" /></Field>
                  <Field label="Destino"><Input placeholder="MIA" value={f.destination} onChange={(e) => updateFlight(i, "destination", e.target.value)} className="rounded-xl bg-white" /></Field>
                  <Field label="Salida"><Input type="time" value={f.departure_time} onChange={(e) => updateFlight(i, "departure_time", e.target.value)} className="rounded-xl bg-white" /></Field>
                  <Field label="Llegada"><Input type="time" value={f.arrival_time} onChange={(e) => updateFlight(i, "arrival_time", e.target.value)} className="rounded-xl bg-white" /></Field>
                  <Field label="Equipaje">
                    <Select value={f.baggage} onValueChange={(v) => updateFlight(i, "baggage", v)}>
                      <SelectTrigger className="rounded-xl bg-white"><SelectValue placeholder="Elegir…" /></SelectTrigger>
                      <SelectContent>{BAGGAGE_OPTIONS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Aerolínea" className="md:col-span-2">
                    <Input placeholder="LATAM, Aerolíneas Argentinas…" value={f.airline} onChange={(e) => updateFlight(i, "airline", e.target.value)} className="rounded-xl bg-white" />
                  </Field>
                  <Field label="Notas" className="md:col-span-4">
                    <Input placeholder="Escala, número de vuelo, etc." value={f.notes} onChange={(e) => updateFlight(i, "notes", e.target.value)} className="rounded-xl bg-white" />
                  </Field>
                </div>
                {f.show_price && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4 pt-4 border-t border-slate-200">
                    <Field label={`Precio por persona (${currencySymbol(form.currency)})`}>
                      <Input type="number" step="0.01" min="0" value={f.price_per_person} onChange={(e) => updateFlight(i, "price_per_person", e.target.value)} className="rounded-xl bg-white" data-testid={`flight-ppp-${i}`} />
                    </Field>
                    <Field label={`Precio total (${currencySymbol(form.currency)})`}>
                      <Input type="number" step="0.01" min="0" value={f.price_total} onChange={(e) => updateFlight(i, "price_total", e.target.value)} className="rounded-xl bg-white" data-testid={`flight-total-${i}`} />
                    </Field>
                    <div className="flex items-end">
                      <p className="text-xs text-slate-500">
                        Auto-calculado: precio × {form.passengers} pasajero{form.passengers > 1 ? "s" : ""}
                      </p>
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </Section>

        <Section
          n={3}
          icon={Hotel}
          title="Hoteles"
          subtitle="Con imágenes, régimen y precios"
          actions={
            <Button onClick={() => set("hotels", [...form.hotels, { ...emptyHotel }])} className="rounded-xl bg-sky-600 hover:bg-sky-700" data-testid="button-add-hotel">
              <Plus size={16} className="mr-1" /> Añadir hotel
            </Button>
          }
        >
          {form.hotels.length === 0 && (
            <p className="text-sm text-slate-500 italic">Aún no hay hoteles agregados.</p>
          )}
          <div className="space-y-4">
            {hotelsForRender.map((h, i) => (
              <Card key={i} className="p-5 border border-slate-200 rounded-2xl bg-slate-50/50" data-testid={`hotel-card-${i}`}>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-xs font-bold uppercase tracking-widest text-sky-600">Hotel #{i + 1}</span>
                  <Button variant="ghost" size="sm" onClick={() => set("hotels", form.hotels.filter((_, idx) => idx !== i))} className="text-rose-600" data-testid={`hotel-remove-${i}`}>
                    <Trash2 size={16} />
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                  <Field label="Etiqueta de opción">
                    <Input placeholder="A, B, C o vacío" maxLength={12} value={h.option_label || ""} onChange={(e) => updateHotel(i, "option_label", e.target.value)} className="rounded-xl bg-white" data-testid={`hotel-option-${i}`} />
                  </Field>
                  <Field label="Ciudad / Grupo">
                    <Input list="hotel-group-options" placeholder="Ej: Punta Cana" value={h.group || ""} onChange={(e) => updateHotel(i, "group", e.target.value)} className="rounded-xl bg-white" data-testid={`hotel-group-${i}`} />
                  </Field>
                  <Field label="Nombre" className="md:col-span-2 lg:col-span-2">
                    <Input placeholder="Hotel Palacio del Mar" value={h.name} onChange={(e) => updateHotel(i, "name", e.target.value)} className="rounded-xl bg-white" data-testid={`hotel-name-${i}`} />
                  </Field>
                  <Field label="Noches"><Input type="number" min="0" value={h.nights} onChange={(e) => updateHotel(i, "nights", e.target.value)} className="rounded-xl bg-white" /></Field>
                  <Field label="Tipo de habitación">
                    <Select value={h.room_type} onValueChange={(v) => updateHotel(i, "room_type", v)}>
                      <SelectTrigger className="rounded-xl bg-white"><SelectValue placeholder="Elegir…" /></SelectTrigger>
                      <SelectContent>{ROOM_TYPES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Régimen alimentación" className="md:col-span-2">
                    <Select value={h.meal_plan} onValueChange={(v) => updateHotel(i, "meal_plan", v)}>
                      <SelectTrigger className="rounded-xl bg-white"><SelectValue placeholder="Elegir…" /></SelectTrigger>
                      <SelectContent>{MEAL_PLANS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label={`Precio por persona (${currencySymbol(form.currency)})`}>
                    <Input type="number" step="0.01" min="0" value={h.price_per_person} onChange={(e) => updateHotel(i, "price_per_person", e.target.value)} className="rounded-xl bg-white" data-testid={`hotel-ppp-${i}`} />
                  </Field>
                  <Field label={`Precio total (${currencySymbol(form.currency)}) — auto`}>
                    <Input type="number" step="0.01" min="0" value={h.price_total} onChange={(e) => updateHotel(i, "price_total", e.target.value)} className="rounded-xl bg-white" data-testid={`hotel-total-${i}`} />
                  </Field>
                  <Field label="Descripción breve" className="md:col-span-2 lg:col-span-4">
                    <Textarea rows={2} placeholder="A 200 mts de la playa, piscina climatizada…" value={h.description} onChange={(e) => updateHotel(i, "description", e.target.value)} className="rounded-xl bg-white" />
                  </Field>
                  <Field label="Imágenes del hotel" className="md:col-span-2 lg:col-span-4">
                    <ImagesInput value={h.images} onChange={(imgs) => updateHotel(i, "images", imgs)} testIdPrefix={`hotel-${i}`} />
                  </Field>
                </div>
              </Card>
            ))}
          </div>
          <datalist id="hotel-group-options">
            {[...new Set(form.hotels.map((h) => (h.group || "").trim()).filter(Boolean))].map((g) => (
              <option key={g} value={g} />
            ))}
          </datalist>
        </Section>

        <Section n={4} icon={Save} title="Notas y total" subtitle="Se incluyen al pie del PDF">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <Field label="Notas adicionales">
                <Textarea rows={4} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Condiciones, políticas, observaciones..." className="rounded-xl bg-white" />
              </Field>
            </div>
            <div className="flex flex-col gap-3">
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-slate-900">Mostrar total cotización</p>
                  <p className="text-xs text-slate-500 mt-1">Desactívalo si vas a enviar varias opciones de hotel y no querés sumar un total.</p>
                </div>
                <Switch checked={!!form.show_total} onCheckedChange={(v) => set("show_total", v)} data-testid="switch-show-total" />
              </div>
              <div className="rounded-xl bg-white border border-slate-200 p-4">
                <p className="text-[10px] uppercase tracking-widest text-slate-400">Total calculado</p>
                <p className="text-2xl font-bold text-amber-600 font-mono-2 mt-1">{fmtMoney(grandTotal, form.currency)}</p>
                <p className="text-[11px] text-slate-500 mt-1">Suma hoteles + vuelos con precio activado.</p>
              </div>
            </div>
          </div>
        </Section>
      </div>

      {/* Sticky bottom bar */}
      <div className="fixed bottom-0 left-0 lg:left-64 right-0 z-30 bg-white/95 backdrop-blur border-t border-slate-200 px-4 sm:px-8 py-3">
        <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-slate-400">Total estimado</p>
            <p className="text-2xl font-bold text-amber-600 font-mono-2">{fmtMoney(grandTotal, form.currency)}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => submit(false)} disabled={saving} className="rounded-xl h-11" data-testid="button-save-quote">
              {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
              Guardar
            </Button>
            <Button onClick={() => submit(true)} disabled={saving} className="rounded-xl h-11 bg-slate-900 hover:bg-slate-800 text-white" data-testid="button-generate-pdf">
              <Download size={16} className="mr-2" /> Guardar y descargar PDF
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteFormPage;
